import React, { useState, useEffect } from 'react';
import { X, ChartLineUp, Lightning, Target, CheckCircle, Warning, ArrowRight, Sparkle, Copy, Check, Brain, PenNib, Lightbulb, CaretDown, CaretUp, Play } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/utils/api';
import { toast } from 'sonner';

const AnalyzeResultsModal = ({ isOpen, onClose, onUseContent, initialContentType, initialContext }) => {
  const [rawMetrics, setRawMetrics] = useState('');
  const [contentType, setContentType] = useState('social_media');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [businessContext, setBusinessContext] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [error, setError] = useState(null);
  
  // Action generation states
  const [activeAction, setActiveAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  // Handle "Use This" action - sends content to parent for content creation
  const handleUseContent = (contentData) => {
    if (onUseContent) {
      onUseContent(contentData);
      onClose();
      toast.success('Opening content creator with your selection!');
    }
  };

  // Content type options
  const contentTypes = [
    { value: 'social_media', label: 'Social Media', icon: '📱', description: 'TikTok, Instagram, YouTube' },
    { value: 'email', label: 'Email Campaign', icon: '📧', description: 'Open rates, clicks, conversions' },
    { value: 'landing_page', label: 'Landing Page', icon: '🎯', description: 'Visits, conversions, bounce' },
    { value: 'sales_page', label: 'Sales Page', icon: '💰', description: 'Sales, conversion rate' },
    { value: 'ad', label: 'Ads', icon: '📢', description: 'CTR, CPC, ROAS' },
    { value: 'general', label: 'Other', icon: '📊', description: 'Any other metrics' },
  ];

  // Fetch business context on open
  useEffect(() => {
    if (isOpen) {
      fetchBusinessContext();
      // Reset state
      setAnalysis(null);
      setAnalysisId(null);
      setError(null);
      setActiveAction(null);
      setActionResult(null);
      setExpandedSection(null);
      
      // Apply initial values if provided (Quick Analysis flow)
      if (initialContentType) {
        setContentType(initialContentType);
      }
    }
  }, [isOpen, initialContentType]);

  const fetchBusinessContext = async () => {
    try {
      const response = await api.get('/analysis/context');
      setBusinessContext(response.data);
    } catch (err) {
      console.error('Failed to fetch business context:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!rawMetrics.trim() || rawMetrics.trim().length < 10) {
      setError('Please enter your performance metrics (at least a few numbers or details)');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const response = await api.post('/analysis/results', {
        raw_metrics: rawMetrics,
        content_type: contentType
      });

      setAnalysis(response.data.analysis);
      setAnalysisId(response.data.id);
      toast.success('Analysis complete!');
    } catch (err) {
      console.error('Analysis failed:', err);
      setError(err.response?.data?.detail || 'Failed to analyze results. Please try again.');
      toast.error('Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    toast.success('Copied to clipboard!');
  };

  // Action generation handlers
  const handleGenerateAction = async (actionType) => {
    setActiveAction(actionType);
    setActionLoading(true);
    setActionResult(null);

    try {
      let endpoint = '';
      let payload = { analysis_id: analysisId };

      switch (actionType) {
        case 'hooks':
          endpoint = '/api/analysis/actions/hooks';
          payload.count = 5;
          break;
        case 'ctas':
          endpoint = '/api/analysis/actions/ctas';
          payload.count = 5;
          break;
        case 'caption':
          endpoint = '/api/analysis/actions/caption';
          break;
        case 'post-idea':
          endpoint = '/api/analysis/actions/post-idea';
          break;
        default:
          return;
      }

      const response = await api.post(endpoint, payload);
      setActionResult({ type: actionType, data: response.data });
      setExpandedSection(actionType);
      toast.success('Generated successfully!');
    } catch (err) {
      console.error('Action generation failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Average';
    if (score >= 30) return 'Needs Work';
    return 'Critical';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <ChartLineUp size={28} weight="bold" className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {initialContentType ? 'Quick Analysis' : 'Analyze My Results'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {initialContentType 
                      ? 'Analyze your post and get instant recommendations' 
                      : 'Get actionable insights from your performance data'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>
            
            {/* Quick Analysis Badge */}
            {initialContentType && (
              <div className="mt-3 flex items-center gap-2 bg-amber-500/20 rounded-lg px-3 py-2">
                <Lightning size={18} className="text-amber-400" weight="fill" />
                <span className="text-amber-100 text-sm">
                  Quick Analysis Mode — just paste your metrics
                </span>
              </div>
            )}
            
            {/* Business Context Indicator */}
            {businessContext && businessContext.has_business_plan && (
              <div className={`${initialContentType ? 'mt-2' : 'mt-4'} flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2`}>
                <Brain size={18} className="text-white/80" />
                <span className="text-white/80 text-sm">
                  Using your Business Brain: <span className="text-white font-medium">{businessContext.niche || 'Your niche'}</span>
                  {businessContext.platform_focus && (
                    <> on <span className="text-white font-medium">{businessContext.platform_focus}</span></>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {!analysis ? (
              /* Input Form */
              <div className="space-y-6">
                {/* Content Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    What type of content are you analyzing?
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {contentTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setContentType(type.value)}
                        className={`p-3 rounded-xl border transition-all text-left ${
                          contentType === type.value
                            ? 'bg-purple-500/20 border-purple-500 text-white'
                            : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500'
                        }`}
                      >
                        <span className="text-2xl mb-1 block">{type.icon}</span>
                        <span className="font-medium block">{type.label}</span>
                        <span className="text-xs text-slate-400">{type.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Metrics Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Paste your performance metrics
                  </label>
                  <textarea
                    value={rawMetrics}
                    onChange={(e) => setRawMetrics(e.target.value)}
                    placeholder={`Example:\n• Video got 1,200 views, 34 likes, 2 comments, 0 shares\n• Watch time: 45%\n• Click-through rate: 1.2%\n\nOr:\n• Email open rate: 18%\n• Click rate: 1.2%\n• 0 sales from 500 sends`}
                    className="w-full h-48 bg-slate-700/50 border border-slate-600 rounded-xl p-4 text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    data-testid="metrics-input"
                  />
                  <p className="mt-2 text-xs text-slate-400">
                    Just paste the numbers - views, likes, comments, shares, watch time, open rate, click rate, conversions, etc.
                  </p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                    {error}
                  </div>
                )}

                {/* Analyze Button */}
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || !rawMetrics.trim()}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="analyze-button"
                >
                  {isAnalyzing ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Sparkle size={20} />
                      </motion.div>
                      Analyzing your results...
                    </>
                  ) : (
                    <>
                      <ChartLineUp size={20} weight="bold" />
                      Analyze My Results
                    </>
                  )}
                </button>
              </div>
            ) : (
              /* Analysis Results */
              <div className="space-y-6" data-testid="analysis-results">
                {/* Performance Score */}
                <div className="bg-slate-700/50 rounded-xl p-6 border border-slate-600">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Performance Score</h3>
                    <div className={`text-3xl font-bold ${getScoreColor(analysis.performance_score)}`}>
                      {analysis.performance_score}/100
                    </div>
                  </div>
                  <div className="w-full bg-slate-600 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        analysis.performance_score >= 70 ? 'bg-green-500' :
                        analysis.performance_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${analysis.performance_score}%` }}
                    />
                  </div>
                  <p className={`mt-2 text-sm ${getScoreColor(analysis.performance_score)}`}>
                    {getScoreLabel(analysis.performance_score)}
                  </p>
                  <p className="mt-3 text-slate-300">{analysis.overall_assessment}</p>
                </div>

                {/* What's Working */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle size={22} className="text-green-400" weight="fill" />
                    <h3 className="text-lg font-semibold text-green-400">What Is Working</h3>
                  </div>
                  <ul className="space-y-2">
                    {analysis.what_is_working.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-300">
                        <span className="text-green-400 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What's Not Working */}
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Warning size={22} className="text-orange-400" weight="fill" />
                    <h3 className="text-lg font-semibold text-orange-400">What Is Not Working</h3>
                  </div>
                  <ul className="space-y-2">
                    {analysis.what_is_not_working.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-slate-300">
                        <span className="text-orange-400 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recommended Next Fix */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={22} className="text-purple-400" weight="fill" />
                    <h3 className="text-lg font-semibold text-purple-400">Recommended Next Fix</h3>
                    <span className="ml-auto px-2 py-1 bg-purple-500/30 rounded text-xs text-purple-300 font-medium">
                      {analysis.recommended_next_fix.priority} PRIORITY
                    </span>
                  </div>
                  <p className="text-white font-medium mb-2">{analysis.recommended_next_fix.issue}</p>
                  <p className="text-slate-300 text-sm mb-4">{analysis.recommended_next_fix.explanation}</p>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-sm font-medium text-slate-400 mb-2">Action Steps:</p>
                    <ol className="space-y-2">
                      {analysis.recommended_next_fix.action_steps.map((step, index) => (
                        <li key={index} className="flex items-start gap-2 text-slate-300">
                          <span className="bg-purple-500/30 text-purple-300 rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                            {index + 1}
                          </span>
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                {/* Improved Hook Examples */}
                <div className="bg-slate-700/50 rounded-xl p-5 border border-slate-600">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightning size={22} className="text-yellow-400" weight="fill" />
                    <h3 className="text-lg font-semibold text-white">Improved Hook Examples</h3>
                  </div>
                  <div className="space-y-3">
                    {analysis.improved_hook_examples.map((hook, index) => (
                      <div key={index} className="bg-slate-800/50 rounded-lg p-4 group">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-white font-medium">{hook.hook}</p>
                            <p className="text-slate-400 text-sm mt-1">{hook.why_it_works}</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(hook.hook, `hook-${index}`)}
                            className="p-2 hover:bg-slate-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            {copiedIndex === `hook-${index}` ? (
                              <Check size={18} className="text-green-400" />
                            ) : (
                              <Copy size={18} className="text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Improved CTA Suggestions */}
                <div className="bg-slate-700/50 rounded-xl p-5 border border-slate-600">
                  <div className="flex items-center gap-2 mb-4">
                    <ArrowRight size={22} className="text-blue-400" weight="bold" />
                    <h3 className="text-lg font-semibold text-white">Improved CTA Suggestions</h3>
                  </div>
                  <div className="space-y-3">
                    {analysis.improved_cta_suggestions.map((cta, index) => (
                      <div key={index} className="bg-slate-800/50 rounded-lg p-4 group">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="text-white font-medium">{cta.cta}</p>
                            <p className="text-slate-400 text-sm mt-1">{cta.context}</p>
                          </div>
                          <button
                            onClick={() => copyToClipboard(cta.cta, `cta-${index}`)}
                            className="p-2 hover:bg-slate-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            {copiedIndex === `cta-${index}` ? (
                              <Check size={18} className="text-green-400" />
                            ) : (
                              <Copy size={18} className="text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Take Action Section */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkle size={22} className="text-emerald-400" weight="fill" />
                    <h3 className="text-lg font-semibold text-emerald-400">Take Action Now</h3>
                  </div>
                  <p className="text-slate-300 text-sm mb-4">
                    Turn this analysis into your next piece of content. PayGBot will use your business context automatically.
                  </p>
                  
                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleGenerateAction('hooks')}
                      disabled={actionLoading}
                      className={`p-3 rounded-xl border transition-all text-left ${
                        activeAction === 'hooks' && actionLoading
                          ? 'bg-yellow-500/20 border-yellow-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-yellow-500/50'
                      }`}
                      data-testid="action-hooks"
                    >
                      <Lightning size={20} className="text-yellow-400 mb-1" weight="fill" />
                      <span className="font-medium text-white block">Better Hooks</span>
                      <span className="text-xs text-slate-400">5 scroll-stopping hooks</span>
                    </button>
                    
                    <button
                      onClick={() => handleGenerateAction('ctas')}
                      disabled={actionLoading}
                      className={`p-3 rounded-xl border transition-all text-left ${
                        activeAction === 'ctas' && actionLoading
                          ? 'bg-blue-500/20 border-blue-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-blue-500/50'
                      }`}
                      data-testid="action-ctas"
                    >
                      <ArrowRight size={20} className="text-blue-400 mb-1" weight="bold" />
                      <span className="font-medium text-white block">Stronger CTAs</span>
                      <span className="text-xs text-slate-400">5 action-driving CTAs</span>
                    </button>
                    
                    <button
                      onClick={() => handleGenerateAction('caption')}
                      disabled={actionLoading}
                      className={`p-3 rounded-xl border transition-all text-left ${
                        activeAction === 'caption' && actionLoading
                          ? 'bg-purple-500/20 border-purple-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-purple-500/50'
                      }`}
                      data-testid="action-caption"
                    >
                      <PenNib size={20} className="text-purple-400 mb-1" weight="fill" />
                      <span className="font-medium text-white block">New Caption</span>
                      <span className="text-xs text-slate-400">Ready-to-post caption</span>
                    </button>
                    
                    <button
                      onClick={() => handleGenerateAction('post-idea')}
                      disabled={actionLoading}
                      className={`p-3 rounded-xl border transition-all text-left ${
                        activeAction === 'post-idea' && actionLoading
                          ? 'bg-green-500/20 border-green-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-green-500/50'
                      }`}
                      data-testid="action-post-idea"
                    >
                      <Lightbulb size={20} className="text-green-400 mb-1" weight="fill" />
                      <span className="font-medium text-white block">Post Idea</span>
                      <span className="text-xs text-slate-400">Complete content idea</span>
                    </button>
                  </div>

                  {/* Loading State */}
                  {actionLoading && (
                    <div className="mt-4 flex items-center justify-center gap-2 text-slate-300">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Sparkle size={18} />
                      </motion.div>
                      <span>Generating based on your analysis...</span>
                    </div>
                  )}

                  {/* Action Results */}
                  {actionResult && !actionLoading && (
                    <div className="mt-4 bg-slate-800/70 rounded-xl p-4 border border-slate-600">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedSection(expandedSection === actionResult.type ? null : actionResult.type)}
                      >
                        <h4 className="font-medium text-white">
                          {actionResult.type === 'hooks' && 'Generated Hooks'}
                          {actionResult.type === 'ctas' && 'Generated CTAs'}
                          {actionResult.type === 'caption' && 'Generated Caption'}
                          {actionResult.type === 'post-idea' && 'Generated Post Idea'}
                        </h4>
                        {expandedSection === actionResult.type ? (
                          <CaretUp size={18} className="text-slate-400" />
                        ) : (
                          <CaretDown size={18} className="text-slate-400" />
                        )}
                      </div>

                      <AnimatePresence>
                        {expandedSection === actionResult.type && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 space-y-3">
                              {/* Hooks Result */}
                              {actionResult.type === 'hooks' && actionResult.data.hooks && (
                                <>
                                  {actionResult.data.hooks.map((hook, idx) => (
                                    <div key={idx} className="bg-slate-700/50 rounded-lg p-3 group">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <p className="text-white font-medium">{hook.hook}</p>
                                          <div className="flex gap-2 mt-1">
                                            <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">{hook.hook_type}</span>
                                            <span className="text-xs text-slate-400">{hook.best_for}</span>
                                          </div>
                                        </div>
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => handleUseContent({ type: 'hook', content: hook.hook, hookType: hook.hook_type })}
                                            className="p-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 rounded transition-colors"
                                            title="Use this hook"
                                            data-testid={`use-hook-${idx}`}
                                          >
                                            <Play size={16} className="text-emerald-400" weight="fill" />
                                          </button>
                                          <button
                                            onClick={() => copyToClipboard(hook.hook, `gen-hook-${idx}`)}
                                            className="p-1.5 hover:bg-slate-600 rounded transition-colors"
                                          >
                                            {copiedIndex === `gen-hook-${idx}` ? (
                                              <Check size={16} className="text-green-400" />
                                            ) : (
                                              <Copy size={16} className="text-slate-400" />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  <p className="text-xs text-slate-400 italic">{actionResult.data.usage_tip}</p>
                                </>
                              )}

                              {/* CTAs Result */}
                              {actionResult.type === 'ctas' && actionResult.data.ctas && (
                                <>
                                  {actionResult.data.ctas.map((cta, idx) => (
                                    <div key={idx} className="bg-slate-700/50 rounded-lg p-3 group">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <p className="text-white font-medium">{cta.cta}</p>
                                          <div className="flex gap-2 mt-1">
                                            <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">{cta.cta_type}</span>
                                            <span className="text-xs text-slate-400">{cta.use_case}</span>
                                          </div>
                                        </div>
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => handleUseContent({ type: 'cta', content: cta.cta, ctaType: cta.cta_type })}
                                            className="p-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 rounded transition-colors"
                                            title="Use this CTA"
                                            data-testid={`use-cta-${idx}`}
                                          >
                                            <Play size={16} className="text-emerald-400" weight="fill" />
                                          </button>
                                          <button
                                            onClick={() => copyToClipboard(cta.cta, `gen-cta-${idx}`)}
                                            className="p-1.5 hover:bg-slate-600 rounded transition-colors"
                                          >
                                            {copiedIndex === `gen-cta-${idx}` ? (
                                              <Check size={16} className="text-green-400" />
                                            ) : (
                                              <Copy size={16} className="text-slate-400" />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  <p className="text-xs text-slate-400 italic">{actionResult.data.placement_tip}</p>
                                </>
                              )}

                              {/* Caption Result */}
                              {actionResult.type === 'caption' && actionResult.data.caption && (
                                <div className="space-y-3">
                                  <div className="bg-slate-700/50 rounded-lg p-4">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <p className="text-white whitespace-pre-wrap">{actionResult.data.caption}</p>
                                      <div className="flex gap-1 flex-shrink-0">
                                        <button
                                          onClick={() => handleUseContent({ 
                                            type: 'caption', 
                                            content: actionResult.data.caption,
                                            hashtags: actionResult.data.hashtags 
                                          })}
                                          className="p-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 rounded transition-colors"
                                          title="Use this caption"
                                          data-testid="use-caption"
                                        >
                                          <Play size={16} className="text-emerald-400" weight="fill" />
                                        </button>
                                        <button
                                          onClick={() => copyToClipboard(actionResult.data.caption, 'gen-caption')}
                                          className="p-1.5 hover:bg-slate-600 rounded transition-colors"
                                        >
                                          {copiedIndex === 'gen-caption' ? (
                                            <Check size={16} className="text-green-400" />
                                          ) : (
                                            <Copy size={16} className="text-slate-400" />
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-3">
                                      {actionResult.data.hashtags?.map((tag, idx) => (
                                        <span key={idx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                                          #{tag}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="flex gap-4 text-xs text-slate-400">
                                    <span>{actionResult.data.character_count} chars</span>
                                    <span>Hook: {actionResult.data.hook_used}</span>
                                    <span>CTA: {actionResult.data.cta_used}</span>
                                  </div>
                                </div>
                              )}

                              {/* Post Idea Result */}
                              {actionResult.type === 'post-idea' && actionResult.data.post_idea && (
                                <div className="space-y-3">
                                  <div className="bg-slate-700/50 rounded-lg p-4">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <h5 className="text-white font-semibold">{actionResult.data.post_idea.title}</h5>
                                      <button
                                        onClick={() => handleUseContent({ 
                                          type: 'post-idea', 
                                          content: actionResult.data.post_idea.hook,
                                          title: actionResult.data.post_idea.title,
                                          outline: actionResult.data.post_idea.content_outline,
                                          cta: actionResult.data.post_idea.cta,
                                          format: actionResult.data.post_idea.format
                                        })}
                                        className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium"
                                        title="Build this post"
                                        data-testid="use-post-idea"
                                      >
                                        <Play size={14} weight="fill" />
                                        Build This
                                      </button>
                                    </div>
                                    <div className="space-y-2">
                                      <div>
                                        <span className="text-xs text-slate-400">Hook:</span>
                                        <p className="text-white">{actionResult.data.post_idea.hook}</p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-slate-400">Content Points:</span>
                                        <ul className="list-disc list-inside text-slate-300 text-sm">
                                          {actionResult.data.post_idea.content_outline.map((point, idx) => (
                                            <li key={idx}>{point}</li>
                                          ))}
                                        </ul>
                                      </div>
                                      <div>
                                        <span className="text-xs text-slate-400">CTA:</span>
                                        <p className="text-white">{actionResult.data.post_idea.cta}</p>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                      <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">{actionResult.data.post_idea.format}</span>
                                      <span className="text-xs bg-slate-600 text-slate-300 px-2 py-0.5 rounded">{actionResult.data.post_idea.estimated_length}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        actionResult.data.viral_potential === 'High' ? 'bg-green-500/20 text-green-300' :
                                        actionResult.data.viral_potential === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                        'bg-slate-600 text-slate-300'
                                      }`}>
                                        {actionResult.data.viral_potential} Viral Potential
                                      </span>
                                    </div>
                                  </div>
                                  <p className="text-sm text-slate-300">{actionResult.data.why_this_works}</p>
                                  <p className="text-xs text-slate-400 italic">Best posting time: {actionResult.data.best_posting_time}</p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* Analyze Another / Create Next Content Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setAnalysis(null);
                      setRawMetrics('');
                    }}
                    className="flex-1 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
                    data-testid="analyze-another-button"
                  >
                    Analyze Another
                  </button>
                  <button
                    onClick={() => {
                      // Pass analysis context for content creation
                      if (onUseContent) {
                        onUseContent({
                          type: 'analysis-improvement',
                          analysisId: analysisId,
                          hook: analysis.improved_hook_examples?.[0]?.hook,
                          cta: analysis.improved_cta_suggestions?.[0]?.cta,
                          issue: analysis.recommended_next_fix?.issue
                        });
                      }
                      onClose();
                      toast.success('Creating content based on your analysis!');
                    }}
                    className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    data-testid="create-next-content-from-analysis"
                  >
                    <Play size={18} weight="fill" />
                    Create Next Content
                  </button>
                </div>
                
                {/* Quick return hint */}
                <p className="text-center text-xs text-slate-500 mt-2">
                  Your analysis will be used to guide your next content
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AnalyzeResultsModal;
